import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Button,
  Label,
  makeStyles,
  tokens,
  useToastController,
  useId,
  Toaster,
} from "@fluentui/react-components";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../redux/store";
import { getCaseCategoryByCaseType } from "../../../../utils";
import CaseNavList from "../CaseNavList";
import CaseForm from "../CaseForm";
import CaseList from "../CaseList";
import ContentLoader from "../../../../components/Loader/ContentLoader";
import ModalDialog from "../../../../components/ModalDialog";
import CaseDetailDrawer from "../CaseDetailDrawer";
import {
  getLiabilityCommissionDistributionDetailById,
  getLiabilityCommissionDistributionListItems,
  getLiabilityDetailById,
  getLiabilityPaymentDetailById,
  getLiabilityPaymentsCommissionDetailById,
  getLiabilityPaymentsCommissionsListItems,
} from "../../../../redux/Case/liabilitySlice";
import { useToastMessage } from "../../../../components/ToastMessage/ToastMessage";
import NoDataFound from "../../../../components/NoDataFound/noDataFound";

const useStyles = makeStyles({});

function LiabilityCommissionDistribution({ tab }: { tab: string }) {
  const navigate = useNavigate();
  const styles = useStyles();
  const { showToast, toasterId } = useToastMessage();
  const { clientId, action, caseId, subItemId, subSubItemId, distributionId } =
    useParams();
  const [activeTab, setActiveTab] = useState<string>(tab || "distribution");

  const dispatch: AppDispatch = useDispatch();
  const [currentRecord, setCurrentRecord] = useState(1);

  const formSettingsObj = useSelector((state: RootState) => state.formSettings);
  const liabilities = useSelector((state: RootState) => state.liabilities);
  const dialogModalOptions = useSelector((state: RootState) => state.dialog);

  const [caseType, setCaseType] = useState<string>("");
  const [isOpen, setIsOpen] = useState(false);
  const handleDetailPopup = (action: boolean) => {
    setIsOpen(action);
  };
  useEffect(() => {
    if (caseId && clientId && subItemId && subSubItemId) {
      dispatch(getLiabilityDetailById({ id: caseId, customerId: clientId }));
      dispatch(getLiabilityPaymentDetailById(subItemId));
      dispatch(
        getLiabilityPaymentsCommissionsListItems({
          paymentId: subItemId,
          caseId,
          customerId: clientId,
        })
      );
      dispatch(
        getLiabilityCommissionDistributionListItems({
          commissionId: subSubItemId,
        })
      );
    }
  }, [caseId, clientId, subItemId, subSubItemId, dispatch]);

  useEffect(() => {
    if (liabilities?.activeItem?.case_type) {
      setCaseType(
        getCaseCategoryByCaseType(liabilities?.activeItem?.case_type)
      );
    }
  }, [liabilities?.activeItem]);

  useEffect(() => {
    if (subItemId && liabilities?.payments?.listItems?.length > 0) {
      dispatch(getLiabilityPaymentDetailById(subItemId));
    }
  }, [subItemId, activeTab, liabilities?.payments?.listItems, dispatch]);
  useEffect(() => {
    if (
      subSubItemId &&
      liabilities?.payments?.commissions?.listItems?.length > 0
    ) {
      dispatch(getLiabilityPaymentsCommissionDetailById(subSubItemId));
    }
  }, [
    subSubItemId,
    activeTab,
    liabilities?.payments?.commissions?.listItems,
    dispatch,
  ]);
  useEffect(() => {
    if (
      distributionId &&
      liabilities?.payments?.commissions?.distribution?.listItems?.length > 0
    ) {
      dispatch(getLiabilityCommissionDistributionDetailById(distributionId));
    }
  }, [
    dispatch,
    subSubItemId,
    distributionId,
    activeTab,
    liabilities?.payments?.commissions?.distribution?.listItems,
  ]);

  const paymentId =
    subItemId || liabilities?.payments?.listItems[0]?.payment?.payment_id;
  const commissionId =
    subSubItemId ||
    liabilities?.payments?.commissions?.listItems[0]?.commission_id;
  const distributionID =
    distributionId ||
    liabilities?.payments?.commissions?.distribution?.listItems[0]
      ?.commission_allocation_id;

  useEffect(() => {
    if (!paymentId || !caseId || !commissionId || !distributionID) return;
    if (action === "view") {
      navigate(
        `/clients/${clientId}/liabilities/${caseId}/payments/${paymentId}/commission/${commissionId}/distribution/${distributionID}/distribution/view`
      );
    } else if (action === "add") {
      navigate(
        `/clients/${clientId}/liabilities/${caseId}/payments/${paymentId}/commission/${commissionId}/distribution/add`
      );
    } else if (action === "edit") {
      navigate(
        `/clients/${clientId}/liabilities/${caseId}/payments/${paymentId}/commission/${commissionId}/distribution/${distributionID}/distribution/edit`
      );
    } else {
      navigate(
        `/clients/${clientId}/liabilities/${caseId}/payments/${paymentId}/commission/${commissionId}/distribution/${distributionID}/distribution/view`
      );
    }
  }, [
    clientId,
    action,
    caseId,
    paymentId,
    commissionId,
    distributionID,
    liabilities?.payments?.commissions?.distribution?.listItems,
    tab,
    navigate,
  ]);

  return (
    <div>
      <Toaster toasterId={toasterId} position="top-end" timeout={5000} />
      <CaseNavList
        subItem="payments"
        section="liabilities"
        tab={tab}
        caseType={caseType}
        caseDetail={liabilities?.payments.activeItem}
        handleDetailPopup={handleDetailPopup}
      />

      <div className="case_container_flex">
        {(action === "add" ||
          liabilities?.payments?.commissions?.distribution?.listItems.length >
            0) && (
          <CaseForm
            mode={action || ""}
            section="liabilities"
            caseType={caseType}
            caseDetail={
              liabilities?.payments?.commissions?.distribution?.activeItem
            }
            caseItemList={
              liabilities?.payments?.commissions?.distribution?.listItems
            }
            tab={tab}
            subItem="payments"
            notify={showToast}
            currentRecord={currentRecord}
            setCurrentRecord={setCurrentRecord}
          />
        )}
        <div>
          {liabilities?.payments?.commissions?.distribution?.commissions
            ?.distribution?.listLoading ? (
            <ContentLoader />
          ) : liabilities?.payments?.commissions?.distribution?.listItems
              ?.length > 0 ? (
            <CaseList
              module="distribution"
              data={liabilities?.payments?.commissions?.distribution?.listItems}
              activeItem={
                liabilities?.payments?.commissions?.distribution?.activeItem
              }
              section="liabilities"
              activeTab={tab}
              currentRecord={currentRecord}
              setCurrentRecord={setCurrentRecord}
            />
          ) : (
            // ✅ Only show NoDataFound if not in "add" mode
            action !== "add" && <NoDataFound />
          )}
        </div>
      </div>

      <CaseDetailDrawer
        isOpen={isOpen}
        caseDetail={liabilities?.activeItem}
        handleDetailPopup={handleDetailPopup}
      />
      <ModalDialog options={dialogModalOptions} />
    </div>
  );
}

export default LiabilityCommissionDistribution;
